import { Column } from "@components/table/table";
import { useNfe } from "@contexts/nfe";
import { TNFeInfNFeDet, TNFeInfNFeDetProdDetExport, TNFeInfNFeDetProdDetExportExportInd } from "@models/TNFe";
import { Tooltip } from "@mui/material";
import { formatToCNPJ } from "brazilian-values";
import Modal from "components/modal/modal";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { DetExportModal } from "./DetExportModal";

const ItemModalFrame = styled.div`
        top: 6px;
        left: -14px;
        width: 100%;
        position: absolute;
        z-index: 10000;

        .modal-body {
            padding: 15px;
            overflow: scroll;
        }
`;

export const StyledDetExportTable = styled.div<{ listLength: number }>`
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: calc(100vh - 140px);

    overflow: hidden;

    position: relative;

    font-size: .85rem;
    color: #000;

    padding-top: 12px;
    padding-bottom: calc(12px + 10px); //12px from padding value plus 10px for tbody translation

    .header-frame {
        width: calc(100% - 12px);
        height: 30px;
        
        padding: 0 12px;

        display: flex;
        justify-content: space-between;
        
        position: relative;

        input {
            margin-right: 8px;
        }
        
        .header-item-frame {
            height: 100%;
            width: 100%;

            display: flex;

            margin-left: 8px;

            :not(:first-of-type) {
                margin-left: 20px;
            }

            ${({ listLength }) => {
        return listLength > 15 ? `
                            :last-child {
                                margin-right: 12px;
                            }` : '';
    }}

            color: var(--color-darkest);

            transition: opacity .2s ease-in;

            :hover {
                cursor: pointer;
                opacity: 1;
            }

            > i {
                margin: auto 0;
                margin-left: 6px;
                margin-right: 4px;
                font-size: .75rem;
                color: var(--color-primary);
            }

            > span {
                margin: auto 0;
                font-weight: bold;
                white-space: nowrap;
            }
        }
        
        .info-columns-frame {
            display: flex;
            width: calc(100% - 202px);

            overflow: hidden;
            margin-right: auto;

            background-color: #ff00006a;
        }

        .group-selection-btn {
            display: flex;
            align-items: center;
            
            margin-left: -6px;
            padding: 0 0 0 6px;
            
            border: 1px solid var(--grey-light);
            border-radius: 6px;

            cursor: pointer;

            background-color: var(--white);

            :active {
                background-color: var(--grey-lightest);
            }
            
            :focus-visible {
                outline: none;
            }

            i {
                margin-right: 8px;
                font-size: .65rem;
            }
        }
    }

    .list-frame {
        height: calc(100% - 12px);
        max-height: calc(100% - 16px);
        width: calc(100% - 2px); // 100% minus the border width

        margin-top: 6px;

        border: 1px solid var(--grey-light);
        border-radius: 8px;

        background-color: var(--white);

        overflow-y: auto;

        position: relative;

        .row-frame {
            min-height: 30px;

            padding: 2px 4px 2px 12px;

            align-items: center;

            :hover {
                background-color: #edffec;
                cursor: pointer;
            }

            .row-item {
                width: auto;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;

                color: var(--grey-medium);
                
                :not(:first-child) {
                    margin-left: 22px;
                }
                
                :first-child {
                    margin-left: 4px;
                }
            }
            
            input {
                margin-right: 8px;
            }
        }

        .action-frame {
            display: flex;
            height: 100%;
            width: fit-content;

            background-color: transparent;
            border: none;
            margin: 0;
            margin-right: 4px;
            padding: 0;

            &.action-disabled {

                > i {
                    color: var(--grey-light);

                    :hover {
                        color: var(--grey-light) !important;
                        cursor: default !important;
                    }
                }
            }

            :hover {
                cursor: pointer;
            }

            :focus-visible {
                outline: none;
            }
        }

        .actions-frame {
            width: 100%;
            min-width: 120px;
            
            margin-left: 10px;
            
            display: flex;
            justify-content: end
        }

        .loading, .end-message {
            height: 40px;
            display: grid;
            place-items: center;
            color: var(--grey-regular);
        }
    }
`;

interface ItemModalProps {
    handleClose: Function
    det: TNFeInfNFeDet
    editMode: boolean
    onUpdateDetExport: (detExports: TNFeInfNFeDetProdDetExport[], det: TNFeInfNFeDet) => void
}

export interface CustomTNFeInfNFeDetProdDetExport {
    id: string | null;
    exportInd?: TNFeInfNFeDetProdDetExportExportInd | null;
    ndraw?: string | null;
    [property: string]: any;
}

interface TableAction {
    html: ReactElement,
    tooltipText: string,
    callback: (detExport: CustomTNFeInfNFeDetProdDetExport) => void,
}

function matchValue(obj: any, value: string): boolean {
    for (const key in obj) {
        return key.includes(value)
    }
    return false;
}

function getFirstNonNull(value: any): any {
    for (const key in value) {
        if (value[key] !== null) {
            return value[key]
        }
    }
    return null;
}

export const defColumns = [
    { header: 'Id', accessor: 'id', isActive: false, minWidth: 0 },
    { header: 'Registro exportação', accessor: 'exportInd.nre', isActive: true, minWidth: 130 },
    { header: 'Chave de acesso NF-e para exportação', accessor: 'exportInd.chNFe', isActive: true, minWidth: 400 },
    { header: 'Quantidade item exportado', accessor: 'exportInd.qExport', isActive: true, minWidth: 130 },
    { header: 'Número ato concessório Drawback', accessor: 'ndraw', isActive: true, minWidth: 230 },
]

export function ItemModal(props: ItemModalProps) {
    const initialDets = (props.det.prod.detExport || []).map(item => ({ ...item, id: crypto.randomUUID() }));

    const [detExportList, setDetExportList] = useState<CustomTNFeInfNFeDetProdDetExport[]>(initialDets);
    const [columns, setColumns] = useState<Column[]>(defColumns);
    const [formValue, setFormValue] = useState<CustomTNFeInfNFeDetProdDetExport>();
    const [removeItem, setRemoveItem] = useState<CustomTNFeInfNFeDetProdDetExport>();


    const actions = useMemo<any[]>(() => [
        { callback: (list) => props.handleClose(), text: 'Cancelar' },
        { callback: (list: TNFeInfNFeDetProdDetExport[]) => props.onUpdateDetExport(list, props.det), text: 'Editar' },
    ], []);

    const tableActions = useMemo<TableAction[]>(() => [
        {
            html: <i className="bi bi-pencil table-action action-positive"></i>,
            callback: (detExport: CustomTNFeInfNFeDetProdDetExport) => setFormValue(detExport),
            tooltipText: 'Editar'
        },
        {
            html: <i className="bi bi-trash table-action action-positive" />,
            callback: (detExport: CustomTNFeInfNFeDetProdDetExport) => setRemoveItem(detExport),
            tooltipText: 'Remover'
        }
    ], [detExportList]);

    const updateDetExports = (detExport: CustomTNFeInfNFeDetProdDetExport): void => {
        setDetExportList(old => {
            if (detExport.id) {
                return old.map(item => item.id === detExport.id ? detExport : item);
            } else {
                return [...old, { ...detExport, id: crypto.randomUUID() }];
            }
        });

        setFormValue(undefined);
    };

    return <ItemModalFrame>
        <Modal
            headerTitle={`Visualizar Item ${props?.det?.nitem}`}
            closeOnClickAway={true}
            handleClose={() => props.handleClose()}
            loading={false}
        >
            <div className="edit-frame">
                <aside className={['bottom-frame', 'edit-border', 'visible'].join(' ')}>
                    <div className="content">
                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código do Produto</span>
                                <input type="text" disabled={true} value={props?.det?.prod.cprod} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código NCM</span>
                                <input type="text" disabled={true} value={props?.det?.prod.ncm} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código CEST</span>
                                <input type="text" disabled={true} value={props?.det?.prod.cest} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Indicador de Escala Relevante</span>
                                <input type="text" disabled={true} value={props?.det?.prod.indEscala} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>CNPJ do Fabricante da Mercadoria</span>
                                <input type="text" disabled={true} value={props?.det?.prod.cnpjFab} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código de Benefício Fiscal na UF</span>
                                <input type="text" disabled={true} value={props?.det?.prod.cbenef} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código EX da TIPI</span>
                                <input type="text" disabled={true} value={props?.det?.prod.extipi} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>CFOP</span>
                                <input type="text" disabled={true} value={props?.det?.prod.cfop} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Outras Despesas Acessórias</span>
                                <input type="text" disabled={true} value={props?.det?.prod.voutro} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor do Desconto</span>
                                <input type="text" disabled={true} value={props?.det?.prod.extipi} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor Total do Frete</span>
                                <input type="text" disabled={true} value={props?.det?.prod.vfrete} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor do Seguro</span>
                                <input type="text" disabled={true} value={props?.det?.prod.vseg} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Indicador de Composição do Valor Total da NF-e</span>
                                <input type="text" disabled={true} value={props?.det?.prod.indTot} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código EAN Comercial</span>
                                <input type="text" disabled={true} value={props?.det?.prod.cean} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Unidade Comercial</span>
                                <input type="text" disabled={true} value={props?.det?.prod.ucom} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Quantidade Comercial</span>
                                <input type="text" disabled={true} value={props?.det?.prod.qcom} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código EAN Tributável</span>
                                <input type="text" disabled={true} value={props?.det?.prod.ceantrib} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Unidade Tributável</span>
                                <input type="text" disabled={true} value={props?.det?.prod.utrib} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Quantidade Tributável</span>
                                <input type="text" disabled={true} value={props?.det?.prod.qtrib} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor unitário de comercialização</span>
                                <input type="text" disabled={true} value={props?.det?.prod.vunCom} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor unitário de tributação</span>
                                <input type="text" disabled={true} value={props?.det?.prod.vunTrib} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Número da FCI</span>
                                <input type="text" disabled={true} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Número do pedido de compra</span>
                                <input type="text" disabled={true} value={props?.det?.prod.nitemPed} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Item do pedido de compra</span>
                                <input type="text" disabled={true} value={props?.det?.prod.nitemPed} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor Aproximado dos Tributos</span>
                                <input type="text" disabled={true} />
                            </div>
                        </div>
                    </div>
                </aside>
                {props.det?.imposto.content.map((imposto, index) => {
                    if (matchValue(imposto.value, 'icms')) {
                        const icms = getFirstNonNull(imposto.value);
                        return <aside key={index} className={['bottom-frame', 'edit-border', 'visible'].join(' ')}>
                            <span className="header">ICMS Normal e ST</span>
                            <div className="content">
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Origem da Mercadoria</span>
                                        <input type="text" disabled={true} value={icms?.orig} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Tributação do ICMS</span>
                                        <input type="text" disabled={true} value={icms?.cst} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Modalidade Definição da BC ICMS NORMAL</span>
                                        <input type="text" disabled={true} value={icms?.modBC} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Base de Cálculo do ICMS Normal</span>
                                        <input type="text" disabled={true} value={icms?.vbc} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Alíquota do ICMS Normal</span>
                                        <input type="text" disabled={true} value={icms?.picms} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor do ICMS Normal</span>
                                        <input type="text" disabled={true} value={icms?.vicms} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor da Base de Cálculo do FCP</span>
                                        <input type="text" disabled={true} value={icms?.vbcfcp} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Percentual do Fundo de Combate à Pobreza (FCP)</span>
                                        <input type="text" disabled={true} value={icms?.pfcp} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor do Fundo de Combate à Pobreza (FCP)</span>
                                        <input type="text" disabled={true} value={icms?.vfcp} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Base de Cálculo do ICMS ST</span>
                                        <input type="text" disabled={true} value={icms?.vbcst} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Alíquota do ICMS ST</span>
                                        <input type="text" disabled={true} value={icms?.picmsst} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor do ICMS ST</span>
                                        <input type="text" disabled={true} value={icms?.vicmsst} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor da Base de Cálculo do FCP retido por Substituição Tributária</span>
                                        <input type="text" disabled={true} value={icms?.vbcfcpst} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Percentual do FCP retido por Substituição Tributária</span>
                                        <input type="text" disabled={true} value={icms?.pfcpst} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor do FCP retido por Substituição Tributária</span>
                                        <input type="text" disabled={true} value={icms?.vfcpst} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Percentual Redução de BC do ICMS ST</span>
                                        <input type="text" disabled={true} value={icms?.predBCST} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Percentual do MVA do ICMS ST</span>
                                        <input type="text" disabled={true} value={icms?.pmvast} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Modalidade Definição da BC ICMS ST</span>
                                        <input type="text" disabled={true} value={icms?.modBCST} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor do ICMS- ST desonerado</span>
                                        <input type="text" disabled={true} value={icms?.vicmsdeson} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Motivo da desoneração do ICMS- ST</span>
                                        <input type="text" disabled={true} value={icms?.motDesICMSST} />
                                    </div>
                                </div>
                            </div>
                        </aside>
                    } else if (matchValue(imposto.value, 'pis')) {
                        const pis = getFirstNonNull(imposto.value)
                        return <aside key={index} className={['bottom-frame', 'edit-border', 'visible'].join(' ')}>
                            <span className="header">PIS</span>
                            <div className="content">
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>CST</span>
                                        <input type="text" disabled={true} value={pis?.cst} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Base de Cálculo</span>
                                        <input type="text" disabled={true} value={pis?.vbc} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Aliquota</span>
                                        <input type="text" disabled={true} value={pis?.ppis} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor</span>
                                        <input type="text" disabled={true} value={pis?.vpis} />
                                    </div>
                                </div>
                            </div>
                        </aside>
                    } else if (matchValue(imposto.value, 'cofins')) {
                        const cofins = getFirstNonNull(imposto.value)
                        return <aside key={index} className={['bottom-frame', 'edit-border', 'visible'].join(' ')}>
                            <span className="header">COFINS</span>
                            <div className="content">
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>CST</span>
                                        <input type="text" disabled={true} value={cofins?.cst} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Base de Cálculo</span>
                                        <input type="text" disabled={true} value={cofins?.vbc} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Aliquota</span>
                                        <input type="text" disabled={true} value={cofins?.pcofins} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor</span>
                                        <input type="text" disabled={true} value={cofins?.vcofins} />
                                    </div>
                                </div>
                            </div>
                        </aside>
                    } else if (matchValue(imposto.value, 'ipi')) {
                        const ipi = getFirstNonNull(imposto.value)
                        return <aside key={index} className={['bottom-frame', 'edit-border', 'visible'].join(' ')}>
                            <span className="header">Imposto Sobre Produtos Industrializados</span>
                            <div className="content">
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Classe de Enquadramento</span>
                                        <input type="text" disabled={true} value={ipi?.vbc} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código de Enquadramento</span>
                                        <input type="text" disabled={true} value={imposto.value.cenq} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código do Selo</span>
                                        <input type="text" disabled={true} value={imposto.value.cselo} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>CNPJ do Produtor</span>
                                        <input type="text" disabled={true} value={imposto.value.cnpjprod && formatToCNPJ(imposto.value.cnpjprod)} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Qtd. Selo</span>
                                        <input type="text" disabled={true} value={imposto.value.qselo} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>CST</span>
                                        <input type="text" disabled={true} value={ipi?.cst} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Qtd Total Unidade Padrão</span>
                                        <input type="text" disabled={true} value={ipi?.qUnid} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor por Unidade</span>
                                        <input type="text" disabled={true} value={ipi?.vUnid} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Valor IPI</span>
                                        <input type="text" disabled={true} value={ipi?.vipi} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Base de Cálculo</span>
                                        <input type="text" disabled={true} value={ipi?.vbc} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Alíquota</span>
                                        <input type="text" disabled={true} value={ipi?.pipi} />
                                    </div>
                                </div>
                            </div>
                        </aside>
                    }
                    return <></>;
                })}
                <aside className={['bottom-frame', 'edit-border', 'visible'].join(' ')}>
                    <span className="header">Informações adicionais do produto</span>
                    <div className="content">
                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Descrição</span>
                                <input type="text" disabled={true} value={props?.det?.infAdProd} />
                            </div>
                        </div>
                    </div>
                </aside>

                <aside className={['bottom-frame', 'edit-border', 'visible'].join(' ')}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                        <span className="header">Detalhe da exportação</span>
                        {props.editMode && <button type="button" className={`icon-button`} style={{ alignItems: 'center' }} onClick={() => setFormValue({ id: null, ndraw: null, exportInd: { chNFe: '', nre: '', qExport: '' } })}>Adicionar</button>}
                    </div>
                    <div className="content">
                        <StyledDetExportTable listLength={detExportList.length}>
                            {/* HEADER */}
                            <section className="header-frame">
                                {columns.filter(column => column.isActive).map((column, index) => {
                                    return <div key={index} className="header-item-frame" style={{ minWidth: column.minWidth }}>
                                        <span>{column.header}</span>
                                    </div>
                                })}
                                {props.editMode &&
                                    <div className="header-item-frame" style={{ minWidth: 130, width: 130, justifyContent: "end", flex: 1 }}>
                                        <span>Ações</span>
                                    </div>
                                }
                            </section>

                            {detExportList.length ? detExportList.map((row, index) => {
                                return (
                                    <section className="list-frame" style={{ display: 'flex', flexDirection: 'column' }} key={'list-frame-' + index}>
                                        <div key={'row-frame-' + index} className="row-frame" style={{ display: 'flex', backgroundColor: 'white' }}>
                                            <div className="row-item" style={{ minWidth: 130, width: '100%' }}>{row.exportInd?.nre || "N/A"}</div>
                                            <div className="row-item" style={{ minWidth: 400, width: '100%' }}>{row.exportInd?.chNFe || "N/A"}</div>
                                            <div className="row-item" style={{ minWidth: 130, width: '100%' }}>{row.exportInd?.qexport || "N/A"}</div>
                                            <div className="row-item" style={{ minWidth: 230, width: '100%' }}>{row.nDraw || row.ndraw || "N/A"}</div>
                                            {props.editMode &&
                                                <div className="actions-frame" style={{ minWidth: 130, width: 130 }}>
                                                    {tableActions.map((action, index) => {
                                                        return <Tooltip title={action.tooltipText} placement="top" key={index}>
                                                            <button className={`action-frame`} key={index} onClick={() => action.callback(row)}>
                                                                {action.html}
                                                            </button>
                                                        </Tooltip>;
                                                    })}
                                                </div>
                                            }
                                        </div>
                                    </section>
                                )
                            }) :
                                <section className="list-frame" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div className="loading">Nenhum resultado encontrado</div>
                                </section>
                            }
                        </StyledDetExportTable>

                        {props.editMode &&
                            <div style={{ display: 'flex', justifyContent: "end" }}>
                                {actions.map(action => <div style={{ display: 'flex', zIndex: 1001 }} key={action.tooltipText}>
                                    <button
                                        className={`icon-button`}
                                        style={{ alignItems: 'center', marginLeft: 10, marginBottom: 10 }}
                                        onClick={(event) => { action.callback(detExportList) }}
                                    >
                                        {action.text}
                                    </button>
                                </div>)}
                            </div>
                        }

                        {formValue && <DetExportModal detExport={formValue} handleClose={() => setFormValue(undefined)} onSave={updateDetExports} />}

                        {removeItem &&
                            <Modal
                                style={{ top: '6px', left: '-14px', width: '100%', position: 'absolute', zIndex: '10000' }}
                                headerTitle="Confirmar remoção"
                                closeOnClickAway={false}
                                handleClose={() => setRemoveItem(undefined)}
                                loading={false}
                                actionButtons={[{
                                    text: 'Cancelar',
                                    actionCallback: () => setRemoveItem(undefined),
                                    positiveAction: false
                                },
                                {
                                    text: 'Confirmar',
                                    actionCallback: () => {
                                        const exports = detExportList.filter(ref => ref.id !== removeItem.id);
                                        setDetExportList(exports);
                                        setRemoveItem(undefined)
                                    },
                                    positiveAction: true
                                }]}>
                                <div style={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
                                    <span>Deseja realmente remover o detalhe da exportação?</span>
                                    <span>Lembrando que os dados alterados para esse registro serão perdidos.</span>
                                </div>
                            </Modal>
                        }
                    </div>
                </aside>
            </div>
        </Modal>
    </ItemModalFrame>
}